import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Container, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { schedulingApi } from "../Components/schedulingApi";
import styles from "./LecturerAttendancePublic.module.scss";

const STATUS_OPTIONS = ["present", "late", "absent"];

export default function LecturerAttendancePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", []);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const [error, setError] = useState("");
  const [context, setContext] = useState(null);
  const [attendance, setAttendance] = useState([]);

  const loadContext = async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const data = await schedulingApi.getLecturerAttendanceContext(token, timezone);
      if (data?.error) {
        setError(data.error);
        setContext(null);
        setAttendance([]);
      } else {
        setContext(data);
        if (!data?.otp?.verified) {
          setContext(data);
          setAttendance([]);
          setError("");
          return;
        }
        const nextAttendance = Array.isArray(data?.attendance) ? data.attendance : [];
        setAttendance(
          nextAttendance.map((row) => ({
            enquiry_id: row.enquiry_id,
            status: (row.status || "present").toLowerCase(),
            enquiry: row.enquiry,
          }))
        );
      }
    } catch (e) {
      setError(e?.message || "Failed to load attendance link details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContext();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateAttendanceStatus = (enquiryId, status) => {
    setAttendance((prev) =>
      prev.map((row) => (row.enquiry_id === enquiryId ? { ...row, status } : row))
    );
  };

  const handleSubmitAttendance = async () => {
    setSubmitting(true);
    setError("");
    try {
      const payload = attendance.map((row) => ({
        enquiry_id: row.enquiry_id,
        status: row.status,
      }));
      const data = await schedulingApi.submitLecturerAttendance(token, payload);
      if (data?.error) {
        setError(data.error);
        return;
      }
      toast.success("Attendance submitted successfully.");
      setSuccessOpen(true);
    } catch (e) {
      setError(e?.message || "Failed to submit attendance.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" />
          <span>Loading attendance details...</span>
        </div>
      </Container>
    );
  }

  if (!context) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error || "Invalid or expired attendance link."}</Alert>
      </Container>
    );
  }
  if (context?.otp?.already_submitted) {
    return (
      <div className={styles.pageWrap}>
        <Container className={styles.shell}>
          <Card className={styles.heroCard}>
            <div className={styles.heroHeader}>
              <div>
                <h4 className={styles.heroTitle}>Attendance Already Submitted</h4>
                <p className={styles.heroSub}>This public attendance link is no longer available.</p>
              </div>
              <span className={styles.metaBadge}>Closed</span>
            </div>
            <div className="p-4">
              <Alert variant="info" className={`${styles.stateBanner} mb-0`}>
                Attendance has already been submitted for this class. Please contact Back Office if changes are needed.
              </Alert>
            </div>
          </Card>
        </Container>
      </div>
    );
  }
  if (!context?.otp?.verified) {
    return <Navigate to={`/lecturer-attendance/${token}`} replace />;
  }

  return (
    <>
      <div className={styles.pageWrap}>
        <Container className={styles.shell}>
          <Card className={styles.heroCard}>
            <div className={styles.heroHeader}>
              <div>
                <h4 className={styles.heroTitle}>Submit Attendance</h4>
                <p className={styles.heroSub}>{context?.event_title || "Class"} - {context?.start_local || "TBC"}</p>
              </div>
              <span className={styles.metaBadge}>{timezone}</span>
            </div>
            <div className="p-3 p-md-4">
              <div className={styles.tableWrap}>
                <Table hover className="mb-0">
                  <thead className={styles.tableHeader}>
                    <tr>
                      <th>Student</th>
                      <th>Email</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendance.map((row) => (
                      <tr key={row.enquiry_id}>
                        <td>{`${row?.enquiry?.first_name || ""} ${row?.enquiry?.last_name || ""}`.trim() || `Student #${row.enquiry_id}`}</td>
                        <td>{row?.enquiry?.email || "—"}</td>
                        <td style={{ maxWidth: 180 }}>
                          <select
                            className="form-select form-select-sm"
                            value={row.status}
                            onChange={(e) => updateAttendanceStatus(row.enquiry_id, e.target.value)}
                            disabled={submitting}
                          >
                            {STATUS_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt[0].toUpperCase() + opt.slice(1)}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className="d-flex gap-2 mt-3">
                <Button variant="outline-secondary" onClick={() => navigate(`/lecturer-attendance/${token}`)} disabled={submitting} className={styles.primaryBtn}>
                  Back
                </Button>
                <Button
                  variant="success"
                  disabled={submitting || attendance.length === 0}
                  onClick={handleSubmitAttendance}
                  className={styles.primaryBtn}
                >
                  {submitting ? <Spinner size="sm" animation="border" /> : "Submit Attendance"}
                </Button>
              </div>
              {error ? <Alert variant="danger" className="mt-3 mb-0">{error}</Alert> : null}
            </div>
          </Card>
        </Container>
      </div>
      <Modal centered show={successOpen} onHide={() => setSuccessOpen(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Attendance Submitted</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Attendance has been recorded. Back Office has been notified to finalize the decision.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setSuccessOpen(false)}>
            Done
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
